import React, { useState, useEffect, useRef, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterClass,
  CharacterSpell,
  Spell,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import LoadingBox from "@components/input/LoadingBox";
import CharacterManageSpellsRow from "@dndComponents/character/spells_and_abilities/CharacterManageSpellsRow";
import FilterBox from "@dndComponents/filtering/FilterBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import PageLinks from "@components/Navigation/PageLinks";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  character: Character;
  char_class: CharacterClass;
  subgroup: "Cantrips" | "Add Spells to Book" | "Prepare Spells" | "Customize" | "Spells" | "";
  extra_spells_mode: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

const page_size = 6;

export default function CharacterManageSpellsGroup({
  character,
  char_class,
  subgroup,
  extra_spells_mode,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready_for_filtered, setReadyForFiltered] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [true_filter, setTrueFilter] = useState<any>({ });
  const [my_spell_list, setMySpellList] = useState<Spell[]>([]);
  const [my_always_known, setMyAlwaysKnown] = useState<Spell[]>([]);
  const [bonus_added, setBonusAdded] = useState<string[]>([]);
  const [my_spell_book_spells, setMySpellBookSpells] = useState<Spell[]>([]);
  const [my_spell_book_always_known, setMySpellBookAlwaysKnown] = useState<Spell[]>([]);
  const [show_spellbook_description, setShowSpellbookDescription] = useState(false);
  // const [show_scrolls, setShowScrolls] = useState(false);
  const [char_spells, setCharSpells] = useState<CharacterSpell[]>([]);
  const [extra_spells, setExtraSpells] = useState<CharacterSpell[]>([]);
  const [prev_subgroup, setPrevSubgroup] = useState(subgroup);
  const [page_num, setPageNum] = useState(0);
  const [known, setKnown] = useState<boolean | undefined>(undefined);
  const [prepared, setPrepared] = useState<boolean | undefined>(undefined);

  const char_options = character.character_options;

  const getSpellsForFilter = useCallback(() => {
    // debugger;
    let filtered: (Spell | CharacterSpell)[] = [];
    if (char_class.spellbook && char_class.spellbook.spellbook) {
      // debugger;
      const char_spellbook = char_class.spellbook;
      const add_spells = ["","Add Spells to Book"].includes(subgroup);
      if (add_spells) {
        let list: Spell[] = [...my_spell_book_spells];
        list = list.filter(o => 
          common_util.anyOverlaps(["",...character.character_options.source_books], o.source_book_ids));
        if (list) {
          filtered = [...list.filter(o => o.level > 0)];
        }
      } else {
        // debugger;
        // const spellbook_spell_ids = char_spellbook.spell_ids;
        let list: (Spell | CharacterSpell)[] = subgroup === "Cantrips" ? [...my_spell_list] : [...char_spellbook.spells]; // [...spells.filter(o => spellbook_spell_ids.includes(o._id))];
        list = list.filter(o => 
          common_util.anyOverlaps(["",...character.character_options.source_books], o.source_book_ids));
        if (list) {
          filtered = [...list];
        }
      }
    } else {
      let list: Spell[] = [...my_spell_list];
      list = list.filter(o => 
        common_util.anyOverlaps(["",...character.character_options.source_books], o.source_book_ids));
      if (list) {
        filtered = [...list];
        if (char_class.cantrips_max <= 0) {
          filtered = filtered.filter(o => o.level >= 1);
        }
      }
    }
    setSpellsForFilter(filtered);
    setLoading(false);
  }, [char_class, character, my_spell_book_spells, my_spell_list, subgroup]);

  const [spells_for_filter, setSpellsForFilter] = useState<(Spell | CharacterSpell)[]>([]);
  const mounted = useRef(false);

  const load = useCallback( async () => {
    const new_spells = data_util.spells; 
    const new_spell_lists = data_util.spell_lists;
    
    let new_my_spells: Spell[] = [];
    let new_my_always_known: Spell[] = [];
    let new_my_spell_book_spells: Spell[] = [];
    let new_my_spell_book_always_known: Spell[] = [];
    const new_bonus_added: string[] = [];
    
    const cc = char_class;
    const spell_list = await api.getSpellList(cc.source_id);
    console.log(spell_list);
    if (cc.spellbook && cc.spellbook.spellbook) {
      const spellbook = cc.spellbook.spellbook;
      const sl_finder = spellbook.spell_list === "ALL" ? new_spell_lists : new_spell_lists.filter(o => o._id === spellbook.spell_list);
      
      for (const list of sl_finder) {
        let always_known: Spell[] = [];
        let list_spells = new_spells.filter(o => 
          api.fitSpellList({ spell: o, spell_list: list, char_options }) && 
          o.level > 0 &&
          (!spellbook.rituals_only || o.ritual) &&
          (!o.level || o.level <= cc.spell_level_max) &&
          (cc.schools.length === 0 || cc.schools.includes("Any") || o.school === null || cc.schools.includes(o.school)));
        for (const esl of cc.extra_spell_lists) {
          const extra_lists = new_spell_lists.filter(o => esl.list_ids.includes(o._id));
          for (const extra_list of extra_lists) {
             
            const extra_list_spells = new_spells.filter(o => 
              api.fitSpellList({ spell: o, spell_list: extra_list, char_options }) && 
              list_spells.filter(o2 => o2._id === o._id).length === 0 &&
              o.level > 0 &&
              (!o.level || o.level <= cc.spell_level_max) &&
              (!o.level || esl.the_levels.includes(o.level)) &&
              (esl.magic_schools.length === 0 || esl.magic_schools.includes("Any") || 
                o.school === null || esl.magic_schools.includes(o.school)));
            list_spells = [...list_spells,...extra_list_spells];
          }
        }
        for (const char_bs of cc.bonus_spells) {
          const bs = char_bs.ff;
          if (bs) {
            const use_spell_ids = bs.spells.filter(o => o.level <= cc.level).map(o => o.spell_id);
            const bspells = new_spells.filter(o => 
              use_spell_ids.includes(o._id) &&
              !list_spells.some(o2 => o2._id === o._id)
            );
            new_bonus_added.push(...bspells.map(o => o._id));
            list_spells.push(...bspells);
            if (bs.always_known) {
              always_known = [...always_known,...bspells];
            }
          }
        }
        
        new_my_spell_book_spells = list_spells;
        new_my_spell_book_always_known = always_known;
      }
      if (cc.knowledge_type === "Spellbook") {
        const sl_finder2 = new_spell_lists.filter(o => 
          cc.spell_list_ids.includes(o._id));
        if (sl_finder2) {
          const always_known: Spell[] = [];
          const list_spells = new_spells.filter(o => 
            o.level === 0 && 
            sl_finder2.filter(o2 => 
              api.fitSpellList({ spell: o, spell_list: o2, char_options })
            ).length > 0 &&
            (cc.schools.length === 0 || cc.schools.includes("Any") || o.school === null || cc.schools.includes(o.school)));
          
          new_my_spells = list_spells;
          new_my_always_known = always_known;
        }
      }
    }
    // console.log(new_my_spells.map(o => o.name));
    if (cc.knowledge_type !== "Spellbook") {
      const sl_finder = new_spell_lists.filter(o => cc.spell_list_ids.includes(o._id));
      let always_known: Spell[] = [];
      // debugger;
      let list_spells = new_spells.filter(o => 
        sl_finder.filter(o2 => 
          api.fitSpellList({ spell: o, spell_list: o2, char_options })
        ).length > 0);
      list_spells = list_spells.filter(o => !o.level || o.level <= cc.spell_level_max);
      list_spells = list_spells.filter(o => 
        (cc.schools.length === 0 || cc.schools.includes("Any") || 
          o.school === null || cc.schools.includes(o.school)));
      for (const esl of cc.extra_spell_lists) {
        const extra_lists = new_spell_lists.filter(o => esl.list_ids.includes(o._id));
        for (const extra_list of extra_lists) {
          const extra_list_spells = new_spells.filter(o => 
            api.fitSpellList({ spell: o, spell_list: extra_list, char_options }) && 
            list_spells.filter(o2 => o2._id === o._id).length === 0 &&
            (!o.level || o.level <= cc.spell_level_max) &&
            (!o.level || esl.the_levels.includes(o.level)) &&
            (esl.magic_schools.length === 0 || esl.magic_schools.includes("Any") || 
              o.school === null || esl.magic_schools.includes(o.school)));
          list_spells = [...list_spells,...extra_list_spells];
        }
      }
      // for (const bs of cc.bonus_spells.filter(o => o.ff && o.ff.name.includes("Genie"))) {
      //   console.log(bs);
      //   console.log(list_spells);
      // }
      // debugger;
      for (const char_bs of cc.bonus_spells) {
        const bs = char_bs.ff;
        // console.log(bs);
        if (bs) {
          const bonus_spell_ids = bs.spells.filter(o => o.level <= cc.level).map(o => o.spell_id);
          const use_spell_ids = bonus_spell_ids.map(o => {
            const use_spell_id = char_bs.selected_spell_id_for_given_id[o];
            return use_spell_id === undefined ? o : use_spell_id;
          });
          const bspells = new_spells.filter(o => 
            use_spell_ids.includes(o._id) // &&
            // !list_spells.some(o2 => o2._id === o._id)
          );
          const new_bspells = bspells.filter(o =>
            !list_spells.some(o2 => o2._id === o._id)
          );
          new_bonus_added.push(...new_bspells.map(o => o._id));
          list_spells.push(...new_bspells);
          if (bs.always_known) {
            always_known = [...always_known,...bspells];
          }
        }
      }
      
      new_my_spells = list_spells;
      new_my_always_known = always_known;
    }
    // console.log(new_my_spells.map(o => o.name));
    setMySpellList(new_my_spells);
    setMyAlwaysKnown(new_my_always_known);
    setMySpellBookSpells(new_my_spell_book_spells);
    setMySpellBookAlwaysKnown(new_my_spell_book_always_known);
    setBonusAdded(new_bonus_added);
    
    if (char_class.spellbook && char_class.spellbook.spellbook) {
      const char_spellbook = char_class.spellbook;
      const add_spells = ["","Add Spells to Book"].includes(subgroup);
      if (add_spells) {
        setCharSpells(char_spellbook.spells);
      } else {
        setCharSpells(char_class.spells);
        setExtraSpells(char_class.extra_spells);
      }
    } else {
      setCharSpells(char_class.spells);
      setExtraSpells(char_class.extra_spells);
    }
    setReadyForFiltered(true);
  }, [char_class, subgroup]);
  
  useEffect(() => {
    if (ready_for_filtered && loading) {
      setReadyForFiltered(false);
      getSpellsForFilter();
    }
  }, [ready_for_filtered, loading, getSpellsForFilter]);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const spellsChanged = () => {
    // load();
    setReloading(true);
  }

  useEffect(() => {
    if (subgroup !== prev_subgroup) {
      setPrevSubgroup(subgroup);
      setLoading(true);
      setReadyForFiltered(true);
    }
  }, [subgroup, prev_subgroup]);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load, character]);

  useEffect(() => {
    character.subscription_holder.subscribe("Spells", mounted, spellsChanged);
  }, [character, spellsChanged]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const update = () => {
    character.changed();
    // These shouldn't need to do full recalculates. Too tired right now to find a way to make it just do the spells.
    character.subscription_holder.spellsChanged();
    character.subscription_holder.recalculate();
  }

  const updateSpellbook = () => {
    // debugger;
    character.changed();
    character.subscription_holder.spellsChanged();
    character.subscription_holder.recalculate();
  }

  const updateFilter = (changed: any) => {
    const the_filter = {...changed};
    // debugger;
    if (the_filter.effect_type) {
      the_filter.effect_types = the_filter.effect_type;
      delete the_filter.effect_type;
    }
    if (the_filter.attack_type) {
      the_filter.attack_types = the_filter.attack_type;
      delete the_filter.attack_type;
    }
    if (the_filter.known !== known) {
      setKnown(the_filter.known);
    }
    if (the_filter.prepared !== prepared) {
      setPrepared(the_filter.prepared);
    }
    delete the_filter.known;
    delete the_filter.prepared;
    // console.log(changed);
    // console.log(the_filter);
    setTrueFilter(the_filter);
    setPageNum(0);
    setReloading(true);
  }

  const addSpell = async (spell: Spell | CharacterSpell, extra = false) => {
    // debugger;
    const new_char_spells = [...char_spells];
    const char_spell = spell instanceof CharacterSpell ? spell : new CharacterSpell();
    if (spell instanceof Spell) {
      await api.loadObject(spell);
      char_spell.copySpell(spell);
      char_spell.connectSource(char_class);
    }
    new_char_spells.push(char_spell);
    setCharSpells(new_char_spells);
    if (extra_spells_mode) {
      setExtraSpells([...extra_spells,char_spell]);
    }
    // setTimeout(() => {
    const char_spellbook = char_class.spellbook;
    if (char_spellbook) {
      const add_spells = ["","Add Spells to Book"].includes(subgroup);
      if (add_spells) {
        await connect_util.addSpellToBook(character, char_spell, char_class, extra || extra_spells_mode);
      } else {
        await connect_util.addSpell(character, char_spell, char_class, extra_spells_mode);
      }
    } else {
      await connect_util.addSpell(character, char_spell, char_class, extra_spells_mode);
    }
      // character.changed();
      // onChange();
    // }, 1000);
  }
  
  const removeSpell = (spell: Spell | CharacterSpell) => {
    // debugger;
    const char_spellbook = char_class.spellbook;
    let char_spell: CharacterSpell | undefined = undefined;
    if (spell instanceof Spell) {
      if (spell.level > 0 && char_spellbook) {
        char_spell = char_spellbook.spells.find(o => o.spell_id === spell._id);
      } else {
        char_spell = char_class.spells.find(o => o.spell_id === spell._id);
        if (!char_spell) {
          char_spell = char_class.extra_spells.find(o => o.spell_id === spell._id);
        }
      }
    } else {
      char_spell = spell;
    }
    // setTimeout(() => {
      if (char_spell) {
        const true_id = char_spell.true_id;
        if (!char_spellbook || !subgroup.includes("Prepare")) {
          setCharSpells(char_spells.filter(o => o.true_id !== true_id));
        }
        if (extra_spells_mode) {
          setExtraSpells(extra_spells.filter(o => o.true_id !== true_id));
        }
        connect_util.removeSpell(character, char_spell, "Class", char_class.source_id);
        const add_spells = ["","Add Spells to Book"].includes(subgroup);
        if (char_spellbook && add_spells) {
          connect_util.removeSpellFromBook(character, char_spell, char_class);
        }
      }
      // character.changed();
      // onChange();
    // }, 1000);
  }

  const renderSpells = () => {
    if (char_class.spellbook && char_class.spellbook.spellbook) {
      const char_spellbook = char_class.spellbook;
      // debugger;
      const add_spells = ["","Add Spells to Book"].includes(subgroup);
      if (add_spells) {
        let filtered = spells_for_filter;
        filtered = api.filter("", filtered, true_filter);
        if (known !== undefined) {
          const known_spell_ids = char_spellbook.spells.map(o => o._id);
          if (known) {
            filtered = filtered.filter(o => known_spell_ids.includes(o._id));
          } else {
            filtered = filtered.filter(o => !known_spell_ids.includes(o._id));
          }
        }
        
        const sorted = filtered.sort((a,b) => { 
          if (a.level === b.level) {
            return a.name < b.name ? -1 : 1;
          }
          return a.level - b.level; 
        });
        const page_count = Math.ceil(sorted.length / page_size);
        const filtered_and_paged = sorted.slice(page_size * page_num, page_size * (page_num + 1));
        return (
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            { filtered_and_paged.map(spell => {
              return (
                <Grid
                  key={spell._id}
                  className="padded"
                  size={{
                    xs: 12
                  }}>
                  <CharacterManageSpellsRow
                    char={character}
                    spell={spell}
                    char_class={char_class}
                    always_known={my_spell_book_always_known}
                    bonus_added={bonus_added}
                    // spell_list={list}
                    char_spellbook={char_spellbook}
                    subgroup={subgroup}
                    // change_time={change_time}
                    processing={false}
                    extra_spells_mode={extra_spells_mode}
                    char_spells={char_spells}
                    extra_spells={extra_spells}
                    onChange={() => {
                      updateSpellbook();
                    }}
                    addSpell={addSpell}
                    removeSpell={removeSpell}
                  />
                </Grid>
              );
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
            </Grid>
          </Grid>
        );
      } else {
        let filtered = spells_for_filter;
        filtered = api.filter("", filtered, true_filter);
        
        if (prepared !== undefined) {
          const prepared_spell_ids = char_class.spells.map(o => o._id);
          if (prepared) {
            filtered = filtered.filter(o => prepared_spell_ids.includes(o._id));
          } else {
            filtered = filtered.filter(o => !prepared_spell_ids.includes(o._id));
          }
        }
        
        const sorted = filtered.sort((a,b) => { 
          if (a.level === b.level) {
            return a.name < b.name ? -1 : 1;
          }
          return a.level - b.level; 
        });
        const page_count = Math.ceil(sorted.length / page_size);
        const filtered_and_paged = sorted.slice(page_size * page_num, page_size * (page_num + 1));
        
        return (
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            { filtered_and_paged.map((spell, key) => {
              return (
                <Grid
                  key={key}
                  className="padded"
                  size={{
                    xs: 12
                  }}>
                  <CharacterManageSpellsRow
                    char={character}
                    spell={spell}
                    char_class={char_class}
                    always_known={my_always_known}
                    bonus_added={bonus_added}
                    // spell_list={list}
                    subgroup={subgroup}
                    // change_time={change_time}
                    processing={false}
                    extra_spells_mode={extra_spells_mode}
                    char_spells={char_class.spells}
                    extra_spells={extra_spells}
                    onChange={() => {
                      update();
                    }}
                    addSpell={addSpell}
                    removeSpell={removeSpell}
                  />
                </Grid>
              );
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
            </Grid>
          </Grid>
        );
      }
    } else {
      let filtered = spells_for_filter;
      filtered = api.filter("", filtered, true_filter);
      if (prepared !== undefined) {
        const prepared_spell_ids = char_class.spells.map(o => o._id);
        if (prepared) {
          filtered = filtered.filter(o => prepared_spell_ids.includes(o._id));
        } else {
          filtered = filtered.filter(o => !prepared_spell_ids.includes(o._id));
        }
      } 
      
      const sorted = filtered.sort((a,b) => { 
        if (a.level === b.level) {
          return a.name < b.name ? -1 : 1;
        }
        return a.level - b.level; 
      });
      const page_count = Math.ceil(sorted.length / page_size);
      const filtered_and_paged = sorted.slice(page_size * page_num, page_size * (page_num + 1));
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { filtered_and_paged.map(spell => {
            // if (spell.name === "Polymorph") {
            //   debugger;
            // }
            return (
              <Grid
                key={spell._id}
                className="padded"
                size={{
                  xs: 12
                }}>
                <CharacterManageSpellsRow
                  char={character}
                  spell={spell}
                  char_class={char_class}
                  always_known={my_always_known}
                  bonus_added={bonus_added}
                  // spell_list={list}
                  // change_time={change_time}
                  subgroup={subgroup}
                  processing={false}
                  extra_spells_mode={extra_spells_mode}
                  char_spells={char_spells}
                  extra_spells={extra_spells}
                  onChange={() => {
                    // debugger;
                    update();
                  }}
                  addSpell={addSpell}
                  removeSpell={removeSpell}
                />
              </Grid>
            );
          })}
          <Grid
            size={{
              xs: 12
            }}>
            <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
          </Grid>
        </Grid>
      );
    }
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    // console.log(spells_for_filter);
    return (
      <Grid container spacing={0} direction="row">
        { ["","Add Spells to Book"].includes(subgroup) && char_class.spellbook && char_class.spellbook.spellbook &&
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <ToggleButtonBox
                name="Show Spellbook Description"
                value={show_spellbook_description}
                onToggle={() => {
                  setShowSpellbookDescription(!show_spellbook_description);
                }}
              />
            </Grid>
            { show_spellbook_description &&
              <>
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <span className="header">Your Spellbook: </span><DisplayStringWithReplacements display_string={ char_class.spellbook.spellbook.learning_message } />
                </Grid>
                <Grid
                  size={{
                    xs: 6
                  }}>
                  { char_class.spellbook.total_unused_free_spells !== 0 ? `Unused Free Spells: ${char_class.spellbook.total_unused_free_spells}` : "" }
                </Grid>
                { char_class.spellbook.modifiers.map((mod, key) => {
                  return (
                    <Grid
                      key={key}
                      size={{
                        xs: 12
                      }}>
                      <span className="header">{ mod.name }: </span><DisplayStringWithReplacements display_string={ mod.learning_message } />
                    </Grid>
                  );
                })}
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <span className="header">Spells Learned for Free:</span> { char_class.spellbook.spells.filter(o => !o.extra).map(o => o.name).join(", ") }
                </Grid>
              </>
            }
          </>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <FilterBox 
            data_type="spell"
            flat
            dont_use_api
            character_options={character.character_options}
            the_filter={{ search_string: [""] }}
            extra_fields={ subgroup === "Add Spells to Book" ? ["known"] : ["prepared"] }
            quick_fields={ subgroup === "Add Spells to Book" ? ["school","spell_level","known"] : ["school","spell_level","prepared"] }
            quick_fields_width={3}
            // log_it
            all_objects={ spells_for_filter }
            onChange={updateFilter}
          />
        </Grid>
        {/* { (!char_class.spellbook || subgroup !== "Cantrips") && renderLevels() } */}
        { renderSpells() }
      </Grid>
    );
  }
}
